import { useMemo, useState } from "react";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { useQpmCatalogListQuery } from "@/services/backend/qpm/catalog/api/catalog-api";
import { CustomerName } from "@/services/backend/customers/components/customer-name";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

export function CatalogSourceSelect({
  value,
  onChange,
  excludeCatalogId,
  customerId,
  includeGlobalCatalogs = true,
}: {
  value: string;
  onChange: (catalogId: string) => void;
  excludeCatalogId?: string;
  customerId?: string;
  includeGlobalCatalogs?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { companyId } = useActiveCompany();
  const {
    data: catalogList,
    isLoading,
    error,
  } = useQpmCatalogListQuery({
    company: { active: true, values: [companyId] },
  });

  const selectableCatalogs = useMemo(
    () =>
      (catalogList?.items ?? []).filter(
        (catalog) =>
          catalog.id !== excludeCatalogId &&
          (!customerId ||
            catalog.customerId === customerId ||
            (includeGlobalCatalogs && !catalog.customerId)),
      ),
    [catalogList?.items, excludeCatalogId, customerId, includeGlobalCatalogs],
  );

  const selectedCatalogName =
    selectableCatalogs.find((catalog) => catalog.id === value)?.name ??
    t("Katalog auswählen");

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {selectedCatalogName}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <Command>
          <CommandInput placeholder={t("Katalog suchen")} className="h-9" />
          <CommandList>
            <CommandEmpty>{t("Kein Katalog gefunden")}</CommandEmpty>
            {!isLoading && !error && (
              <CommandGroup>
                {selectableCatalogs.map((catalog) => (
                  <CommandItem
                    key={catalog.id}
                    value={`${catalog.name} ${catalog.description}`}
                    onSelect={() => {
                      onChange(catalog.id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === catalog.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div className="flex flex-col">
                      <span>{catalog.name}</span>
                      <span className="text-muted-foreground text-xs">
                        {catalog.customerId && (
                          <>
                            <CustomerName customerId={catalog.customerId} />
                            <span> - </span>
                          </>
                        )}
                        {catalog.description || t("Ohne Beschreibung")}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {isLoading && (
              <div className="text-muted-foreground px-3 py-2 text-sm">
                {t("Lade Kataloge")}
              </div>
            )}
            {error && (
              <div className="text-destructive px-3 py-2 text-sm">
                {t("Fehler beim Laden der Kataloge")}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
